import { createStore, createEvent, sample, createEffect } from 'effector'
import { getIsMobile } from 'hooks/use-is-mobile'
import { devicesService } from 'features/devices.service'
import { attach } from 'effector/effector.umd'
import { interviewFixturesService } from 'features/interview/interview-fixtures.service'
import { callWsService } from './call-ws.service'

type StartRecordingParams = {
  aivyVoiceStream: MediaStream
  getVideoEl: () => HTMLVideoElement | null
}

type WorkingStore = {
  micStream: MediaStream | null
  recordingParams: StartRecordingParams | null
}

type CreateRecorderFxParams = {
  wsConnected: boolean
  recordingParams: StartRecordingParams | null
  doCaptureCamera: boolean
  orderedTracks: Array<MediaStreamTrack> | null
}

export type UploadChunkData = {
  interviewId: string
  file: Blob
}

export class VideoRecorderService {
  readonly start = createEvent<StartRecordingParams>()
  readonly stop = createEvent<void>()

  private readonly $recordingParams = createStore<StartRecordingParams | null>(
    null
  )
  private readonly $orderedTracks = createStore<Array<MediaStreamTrack> | null>(
    null
  )
  readonly $recorder = createStore<MediaRecorder | null>(null)

  private unsubscribeRecorder: VoidFunction | null = null
  private mediaStream: MediaStream | null = null

  private readonly chunkReceived = createEvent<BlobEvent>()
  readonly doUploadChunk = createEvent<UploadChunkData>()

  private recordingCanvas: HTMLCanvasElement | null = null
  private canvasAnimation: number | null = null

  private isLastDataRequested = false

  private audioContext: AudioContext | null = null

  constructor() {
    this.attachCombinedStreamLogic()
    this.attachRecorderLogic()
    this.attachChunkLogic()
  }

  private attachCombinedStreamLogic = (): void => {
    const state = {
      micStream: devicesService.microphone.$stream,
      recordingParams: this.$recordingParams
    }

    const stopStreamFx = createEffect(this.stopStreamFxImpl)

    const createTracksFx = attach({
      source: state,
      effect: this.createTracksFxImpl
    })

    const triggerCreateStream = createEvent<void>()
    const debounceTimeout = createStore<number>(-1)

    sample({
      clock: triggerCreateStream,
      source: { timeout: debounceTimeout },
      fn: ({ timeout }) => {
        if (timeout) window.clearTimeout(timeout)

        return window.setTimeout(() => {
          void createTracksFx()
        }, 2000)
      },
      target: debounceTimeout
    })

    sample({
      clock: this.start,
      target: this.$recordingParams
    })

    sample({
      clock: this.stop,
      fn: () => null,
      target: this.$recordingParams
    })

    sample({
      source: state,
      target: [stopStreamFx, triggerCreateStream]
    })

    sample({
      clock: createTracksFx.doneData,
      fn: (stream) => stream ?? null,
      target: this.$orderedTracks
    })
  }

  private attachRecorderLogic = (): void => {
    const stopRecorderFx = attach({
      source: this.$recorder,
      effect: this.stopRecorderFxImpl
    })

    const $wsConnected = callWsService.$store.map((x) => x.connected)

    const createRecorderFx = attach({
      source: {
        recordingParams: this.$recordingParams,
        orderedTracks: this.$orderedTracks,
        doCaptureCamera: devicesService.camera.$deviceEnabled,
        wsConnected: $wsConnected
      },
      effect: this.createRecorderFxImpl
    })

    sample({
      clock: [this.$orderedTracks, $wsConnected],
      target: [stopRecorderFx, createRecorderFx]
    })

    sample({
      clock: createRecorderFx.doneData,
      target: this.$recorder
    })
  }

  private stopStreamFxImpl = async (): Promise<void> => {
    if (this.mediaStream) {
      this.mediaStream.onaddtrack = null
      this.mediaStream.getTracks().forEach((t) => t.stop())
    }

    if (this.audioContext) {
      try {
        await this.audioContext.close()
      } catch {
        // noop
      }
      this.audioContext = null
    }
  }

  private stopRecorderFxImpl = (recorder: MediaRecorder | null): void => {
    if (!recorder) return

    if (!this.isLastDataRequested) {
      this.isLastDataRequested = true
      recorder.requestData()
    }

    this.unsubscribeRecorder?.()
    this.unsubscribeRecorder = null

    recorder.stop()
    this.stopCanvas()
  }

  private createTracksFxImpl = async ({
    recordingParams,
    micStream
  }: WorkingStore): Promise<Array<MediaStreamTrack> | null | undefined> => {
    if (!recordingParams) return null

    const { aivyVoiceStream } = recordingParams
    const videoTrack = this.startCanvas()

    if (videoTrack.readyState !== 'live') {
      console.log('recorder: (stream) video is not live')
      return null
    }

    if (aivyVoiceStream.getAudioTracks()[0]?.readyState !== 'live') {
      console.log('recorder: (stream) aivy is not live')
      return null
    }

    if (this.audioContext) {
      void this.audioContext.close()
    }

    this.audioContext = new AudioContext()
    const dest = this.audioContext.createMediaStreamDestination()

    if (micStream) {
      this.audioContext.createMediaStreamSource(micStream).connect(dest)
    } else {
      this.createSilentSource(this.audioContext).connect(dest)
    }

    this.audioContext
      .createMediaStreamSource(recordingParams.aivyVoiceStream)
      .connect(dest)

    const audioTrack = dest.stream.getAudioTracks()[0]
    return [audioTrack, videoTrack]
  }

  private createSilentSource(
    audioContext: AudioContext
  ): AudioBufferSourceNode {
    const silence = audioContext.createBufferSource()
    silence.buffer = audioContext.createBuffer(1, 1, audioContext.sampleRate)
    silence.loop = true
    silence.start()
    return silence
  }

  private createRecorderFxImpl = async ({
    wsConnected,
    orderedTracks,
    doCaptureCamera,
    recordingParams
  }: CreateRecorderFxParams) => {
    if (!wsConnected) {
      console.log(`recorder: WS not connected, skip create`)
      return null
    }

    if (!orderedTracks?.length) {
      console.log(
        `recorder: tracks wrong length, expected 2, got ${orderedTracks?.length}`
      )
      return null
    }

    const videoTrack = orderedTracks.find((x) => x.kind === 'video')
    const audioTrack = orderedTracks.find((x) => x.kind === 'audio')

    if (!videoTrack) {
      console.log('recorder: video track not found', orderedTracks)
      return null
    }
    if (!audioTrack) {
      console.log('recorder: audio track not found', orderedTracks)
      return null
    }
    if (videoTrack.readyState !== 'live') {
      console.log('recorder: video is not live, invalid stream')
      return null
    }
    if (audioTrack.readyState !== 'live') {
      console.log('recorder: audio is not live, invalid stream')
      return null
    }

    console.log('recorder: initializing')

    this.isLastDataRequested = false

    if (this.mediaStream) {
      this.mediaStream.onaddtrack = null
      this.mediaStream.getTracks().forEach((t) => t.stop())
    }

    this.mediaStream = new MediaStream([audioTrack, videoTrack])

    const recorder = new MediaRecorder(this.mediaStream, {
      mimeType: 'video/webm;codecs=vp8,opus',
      videoBitsPerSecond: getIsMobile() ? 250_000 : 500_000,
      audioBitsPerSecond: 64_000
    })

    const handleRequestBeforeUnload = (event: BeforeUnloadEvent) => {
      if (this.isLastDataRequested) return
      this.isLastDataRequested = true

      if (recorder.state === 'recording') {
        recorder.requestData()
        console.log('before unload while recording - request data')

        event.preventDefault()
        event.returnValue = '' // обязательно для Chrome
        return '' // для старых браузеров
      }
    }

    window.addEventListener('beforeunload', handleRequestBeforeUnload)
    window.addEventListener('pagehide', handleRequestBeforeUnload)

    this.unsubscribeRecorder = (): void => {
      window.removeEventListener('beforeunload', handleRequestBeforeUnload)
      window.removeEventListener('pagehide', handleRequestBeforeUnload)
    }

    recorder.ondataavailable = (event) => {
      // if (this.isLastDataRequested) {
      //   console.log('last data available', event.data.size)
      // } else {
      //   console.log('data available', event.data.size)
      // }

      if (event.data.size > 0) {
        this.chunkReceived(event)
      }
    }

    // запуск canvas-рендера
    this.renderToCanvas(recordingParams?.getVideoEl(), doCaptureCamera)
    recorder.start(5000)
    return recorder
  }

  private stopCanvas(): void {
    if (!this.canvasAnimation) return

    cancelAnimationFrame(this.canvasAnimation)
    this.canvasAnimation = null
  }

  private startCanvas(): MediaStreamTrack {
    const canvas = document.createElement('canvas')
    canvas.width = 640
    canvas.height = 480
    this.recordingCanvas = canvas

    const fps = 15
    return canvas.captureStream(fps).getVideoTracks()[0]
  }

  private renderToCanvas(
    videoEl: HTMLVideoElement | undefined | null,
    doCaptureCamera: boolean
  ) {
    const canvas = this.recordingCanvas
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const renderFrame = (): void => {
      this.canvasAnimation = requestAnimationFrame(renderFrame)

      ctx.fillStyle = 'black'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      if (!doCaptureCamera || !videoEl) return

      const videoAspect = videoEl.videoWidth / videoEl.videoHeight
      const canvasAspect = canvas.width / canvas.height

      let dx = 0
      let dy = 0
      let dWidth = canvas.width
      let dHeight = canvas.height

      if (videoAspect > canvasAspect) {
        dWidth = canvas.height * videoAspect
        dx = (canvas.width - dWidth) / 2
      } else {
        dHeight = canvas.width / videoAspect
        dy = (canvas.height - dHeight) / 2
      }

      ctx.drawImage(
        videoEl,
        0,
        0,
        videoEl.videoWidth,
        videoEl.videoHeight,
        dx,
        dy,
        dWidth,
        dHeight
      )
    }

    renderFrame()
  }

  private attachChunkLogic = (): void => {
    sample({
      clock: this.chunkReceived,
      source: interviewFixturesService.$interviewId,
      filter: Boolean,
      fn: (interviewId, event) => {
        return { interviewId, file: event.data }
      },
      target: this.doUploadChunk
    })
  }

  // readonly resumeRecorder = createEvent<void>()

  // private startListening = (mic: WorkingStore): void => {
  //   const { mediaRecorder } = this
  //   if (!mediaRecorder) return
  //
  //   if (mediaRecorder.state === 'inactive' && mic.isEnabled) {
  //     mediaRecorder.start(CHUNK_MS)
  //   }
  //   if (mediaRecorder.state === 'paused' && mic.isEnabled) {
  //     mediaRecorder.resume()
  //   }
  // }

  // sample({
  //   clock: this.resumeRecorder,
  //   source: $workingStore,
  //   filter: Boolean,
  //   target: createEffect((store: WorkingStore) => {
  //     this.startListening(store)
  //   })
  // })
}

export const videoRecorderService = new VideoRecorderService()
